/*
  AI swatch fallback (Elilhaam)
  -----------------------------
  Any colour swatch whose name is not in the theme Swatch color list and not in
  snippets/ai-swatch-map.liquid would show blank white. This script reads that
  variant's photo, finds the garment's main colour and paints the swatch with it.
  Order of priority: theme Swatch color list > ai-swatch-map (AI) > this script.
*/
(function () {
  'use strict';

  var CACHE_KEY = 'elilhaamSwatchCache_v1';
  var cache = {};
  try {
    cache = JSON.parse(window.localStorage.getItem(CACHE_KEY) || '{}') || {};
  } catch (e) {
    cache = {};
  }

  function saveCache() {
    try {
      window.localStorage.setItem(CACHE_KEY, JSON.stringify(cache));
    } catch (e) { /* storage unavailable */ }
  }

  function hasColour(el) {
    var cs = window.getComputedStyle(el);
    var c = (cs.getPropertyValue('--swatch-background-color') || '').trim().toLowerCase();
    var i = (cs.getPropertyValue('--swatch-background-image') || '').trim().toLowerCase();
    var noColour = c === '' || c === 'transparent' || c === 'rgba(0, 0, 0, 0)' || c === 'initial';
    var noImage = i === '' || i === 'none' || i === 'initial';
    return !(noColour && noImage);
  }

  function cacheKeyFor(url) {
    return String(url).replace(/^https?:/, '').replace(/([?&])v=\d+&?/, '$1');
  }

  function toHex(r, g, b) {
    function h(n) {
      var s = Math.round(n).toString(16);
      return s.length === 1 ? '0' + s : s;
    }
    return '#' + h(r) + h(g) + h(b);
  }

  // Finds the main garment colour: samples the torso/hip area of the photo,
  // ignores pixels that match the studio background, then takes the most common colour.
  function dominantColour(img) {
    var w = 60;
    var h = Math.max(1, Math.round(w * (img.naturalHeight / img.naturalWidth)));
    var canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    var ctx = canvas.getContext('2d', { willReadFrequently: true });
    ctx.drawImage(img, 0, 0, w, h);
    var d = ctx.getImageData(0, 0, w, h).data;

    function at(x, y) {
      var i = (y * w + x) * 4;
      return [d[i], d[i + 1], d[i + 2], d[i + 3]];
    }

    // Background estimate from the image border.
    var bg = [0, 0, 0];
    var bn = 0;
    var x;
    var y;
    var p;
    for (x = 0; x < w; x++) {
      p = at(x, 0); bg[0] += p[0]; bg[1] += p[1]; bg[2] += p[2]; bn++;
      p = at(x, 1); bg[0] += p[0]; bg[1] += p[1]; bg[2] += p[2]; bn++;
    }
    for (y = 0; y < h; y++) {
      p = at(0, y); bg[0] += p[0]; bg[1] += p[1]; bg[2] += p[2]; bn++;
      p = at(w - 1, y); bg[0] += p[0]; bg[1] += p[1]; bg[2] += p[2]; bn++;
    }
    bg = [bg[0] / bn, bg[1] / bn, bg[2] / bn];

    function dist(a, b) {
      var dr = a[0] - b[0];
      var dg = a[1] - b[1];
      var db = a[2] - b[2];
      return Math.sqrt(dr * dr + dg * dg + db * db);
    }

    var x0 = Math.round(w * 0.38);
    var x1 = Math.round(w * 0.62);
    var y0 = Math.round(h * 0.30);
    var y1 = Math.round(h * 0.62);
    var all = [];
    var garment = [];
    for (y = y0; y < y1; y++) {
      for (x = x0; x < x1; x++) {
        p = at(x, y);
        if (p[3] < 128) continue;
        all.push(p);
        if (dist(p, bg) > 30) garment.push(p);
      }
    }
    if (!all.length) return null;
    var pool = garment.length >= all.length * 0.15 ? garment : all;

    // Most common colour bucket.
    var bins = {};
    var bestKey = null;
    var bestCount = 0;
    pool.forEach(function (q) {
      var k = (q[0] >> 4) + '-' + (q[1] >> 4) + '-' + (q[2] >> 4);
      bins[k] = (bins[k] || 0) + 1;
      if (bins[k] > bestCount) { bestCount = bins[k]; bestKey = k; }
    });
    var centre = bestKey.split('-').map(function (n) { return parseInt(n, 10) * 16 + 8; });

    // Average every pixel close to that bucket (keeps fabric shading, drops embellishment).
    var sum = [0, 0, 0];
    var n = 0;
    pool.forEach(function (q) {
      if (dist(q, centre) <= 40) { sum[0] += q[0]; sum[1] += q[1]; sum[2] += q[2]; n++; }
    });
    if (!n) return toHex(centre[0], centre[1], centre[2]);
    return toHex(sum[0] / n, sum[1] / n, sum[2] / n);
  }

  var pending = {};

  function colourFromImage(url, done) {
    var key = cacheKeyFor(url);
    if (cache[key]) { done(cache[key]); return; }
    if (pending[key]) { pending[key].push(done); return; }
    pending[key] = [done];
    var img = new Image();
    img.crossOrigin = 'anonymous';
    img.decoding = 'async';
    img.onload = function () {
      var hex = null;
      try {
        hex = dominantColour(img);
      } catch (e) {
        hex = null;
      }
      if (hex) { cache[key] = hex; saveCache(); }
      var cbs = pending[key];
      delete pending[key];
      cbs.forEach(function (cb) { cb(hex); });
    };
    img.onerror = function () {
      var cbs = pending[key];
      delete pending[key];
      cbs.forEach(function (cb) { cb(null); });
    };
    img.src = url.indexOf('//') === 0 ? window.location.protocol + url : url;
  }

  function paint(el, hex) {
    if (!hex) return;
    el.style.setProperty('--swatch-background-color', hex);
    el.style.setProperty('--swatch-background-image', 'none');
    el.setAttribute('data-swatch-ai', hex);
  }

  function findImageFor(el) {
    var own = el.getAttribute('data-swatch-img');
    if (own) return own;
    var name = el.getAttribute('data-swatch');
    if (!name) return null;
    var others = document.querySelectorAll('[data-swatch-img]');
    for (var i = 0; i < others.length; i++) {
      if (others[i].getAttribute('data-swatch') === name) return others[i].getAttribute('data-swatch-img');
    }
    return null;
  }

  function scan() {
    var nodes = document.querySelectorAll('[data-swatch]:not([data-swatch=""]):not([data-swatch-ai])');
    Array.prototype.forEach.call(nodes, function (el) {
      if (hasColour(el)) { el.setAttribute('data-swatch-ai', 'skip'); return; }
      var url = findImageFor(el);
      if (!url) return;
      el.setAttribute('data-swatch-ai', 'pending');
      colourFromImage(url, function (hex) {
        if (hex) paint(el, hex);
        else el.setAttribute('data-swatch-ai', 'failed');
      });
    });
  }

  var timer = null;
  function scheduleScan() {
    if (timer) clearTimeout(timer);
    timer = setTimeout(scan, 150);
  }

  function whenSwatchCssReady(cb) {
    var called = false;
    function go() {
      if (called) return;
      called = true;
      cb();
    }
    var link = document.querySelector('link[href*="swatches.css"]');
    if (!link || (link.sheet && link.media !== 'print')) { go(); return; }
    link.addEventListener('load', function () { setTimeout(go, 0); });
    setTimeout(go, 3000);
  }

  function start() {
    whenSwatchCssReady(function () {
      scan();
      if ('MutationObserver' in window) {
        new MutationObserver(function (mutations) {
          for (var i = 0; i < mutations.length; i++) {
            if (mutations[i].addedNodes && mutations[i].addedNodes.length) { scheduleScan(); return; }
          }
        }).observe(document.body, { childList: true, subtree: true });
      }
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }
})();
